import type {
  OrganizationMember,
  OrganizationMemberInput,
} from '@skop-app/lib-domain/types'
import type { GraphQLResult } from 'aws-amplify/api'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  assignSeatMutation,
  createOrganizationMutation,
  getOrganizationSummaryQuery,
} from '@/services/graphql/team'
import type { OrganizationMutationInput } from '@/services/mutations/team'

const STORAGE_PREFIX = 'skop:team-creation-attempt:v1:'
const client = getAmplifyClient()
const activeUsers = new Set<string>()
const DEFINITIVE_PREWRITE_CREATE_ERROR_TYPES = new Set([
  'Unauthorized',
  'ValidationError',
])

type AttemptPhase = 'organization_unconfirmed' | 'seat_pending'

type StoredAttempt = {
  version: 1
  userId: string
  organizationId: string
  memberId: string
  organizationName: string
  organizationEmail?: string
  memberEmail?: string
  phase: AttemptPhase
}

export type PendingTeamCreationAttempt = Pick<
  StoredAttempt,
  'organizationId' | 'organizationName' | 'memberId' | 'phase'
>

type FinalizeTeamCreation = (
  attempt: PendingTeamCreationAttempt,
) => Promise<void>

export class TeamCreationRecoveryBlockedError extends Error {
  constructor(public readonly organizationId: string) {
    super(
      `La création de l'équipe ${organizationId} doit être vérifiée avant reprise.`,
    )
    this.name = 'TeamCreationRecoveryBlockedError'
  }
}

const storageKey = (userId: string) => `${STORAGE_PREFIX}${userId}`

const getStorage = () => {
  if (typeof window === 'undefined') throw new Error('Session indisponible')
  return window.sessionStorage
}

const readStoredAttempt = (userId: string): StoredAttempt | null => {
  const raw = getStorage().getItem(storageKey(userId))
  if (!raw) return null
  try {
    const attempt = JSON.parse(raw) as Partial<StoredAttempt>
    if (
      attempt.version !== 1 ||
      attempt.userId !== userId ||
      !attempt.organizationId ||
      !attempt.memberId ||
      !attempt.organizationName ||
      (attempt.phase !== 'organization_unconfirmed' &&
        attempt.phase !== 'seat_pending')
    ) {
      throw new Error('Invalid attempt')
    }
    return attempt as StoredAttempt
  } catch {
    // A damaged marker is still a possible unfinished creation. Never overwrite it.
    throw new Error('Tentative de création illisible dans cette session')
  }
}

const saveAttempt = (attempt: StoredAttempt) => {
  getStorage().setItem(storageKey(attempt.userId), JSON.stringify(attempt))
}

export const readPendingTeamCreationAttempt = (
  userId: string,
): PendingTeamCreationAttempt | null => {
  const attempt = readStoredAttempt(userId)
  return attempt
    ? {
        organizationId: attempt.organizationId,
        organizationName: attempt.organizationName,
        memberId: attempt.memberId,
        phase: attempt.phase,
      }
    : null
}

const assignInitialSeat = async (attempt: StoredAttempt) => {
  await client.graphql({
    query: assignSeatMutation,
    variables: {
      input: {
        id: attempt.memberId,
        userID: attempt.userId,
        email: attempt.memberEmail,
        organizationID: attempt.organizationId,
        role: 'ADMIN',
        status: 'DONE',
        type: 'ORGANIZATION',
        onSeat: true,
      },
    },
  })
}

const completeAttempt = async (
  attempt: StoredAttempt,
  finalize?: FinalizeTeamCreation,
) => {
  await finalize?.({
    organizationId: attempt.organizationId,
    organizationName: attempt.organizationName,
    memberId: attempt.memberId,
    phase: attempt.phase,
  })
  getStorage().removeItem(storageKey(attempt.userId))
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object'

const isDefinitiveOrganizationCreateRejection = (error: unknown) => {
  if (!isRecord(error)) return false
  const cause = isRecord(error.cause) ? error.cause : undefined
  const errors = Array.isArray(error.errors)
    ? error.errors
    : Array.isArray(cause?.errors)
      ? cause.errors
      : null
  if (!errors?.length) return false

  // The quota-enforcement createOrganization resolver emits Unauthorized only
  // before its atomic transaction or after a transaction cancellation. AppSync
  // request validation also runs before the resolver. Keep all other errors
  // retryable because their write outcome is not established by the client.
  return errors.every((item) => {
    if (!isRecord(item)) return false
    const extensions = isRecord(item.extensions) ? item.extensions : undefined
    return [item.errorType, extensions?.errorType, extensions?.code].some(
      (type) =>
        typeof type === 'string' &&
        DEFINITIVE_PREWRITE_CREATE_ERROR_TYPES.has(type),
    )
  })
}

export const createTeamWithRecovery = async (
  {
    organization,
    organizationMember,
  }: {
    organization: OrganizationMutationInput
    organizationMember: OrganizationMember | OrganizationMemberInput
  },
  finalize?: FinalizeTeamCreation,
) => {
  const userId = organizationMember.userID
  if (!userId || !organization.id || !organizationMember.id) {
    throw new Error('Identifiants de création manquants')
  }
  if (
    organization.parentID ||
    organizationMember.organizationID !== organization.id
  ) {
    throw new Error('Portée de création incohérente')
  }
  if (activeUsers.has(userId) || readStoredAttempt(userId)) {
    throw new TeamCreationRecoveryBlockedError(organization.id)
  }

  const attempt: StoredAttempt = {
    version: 1,
    userId,
    organizationId: organization.id,
    memberId: organizationMember.id,
    organizationName: organization.name,
    organizationEmail: organization.email,
    memberEmail: organizationMember.email,
    phase: 'organization_unconfirmed',
  }

  activeUsers.add(userId)
  try {
    saveAttempt(attempt)
    try {
      await client.graphql({
        query: createOrganizationMutation,
        variables: {
          input: {
            ...organization,
            status: organization.status ?? 'ACTIVE',
          },
        },
      })
    } catch (error) {
      if (isDefinitiveOrganizationCreateRejection(error)) {
        getStorage().removeItem(storageKey(userId))
      }
      throw error
    }
    attempt.phase = 'seat_pending'
    saveAttempt(attempt)
    await assignInitialSeat(attempt)
    await completeAttempt(attempt, finalize)
  } finally {
    activeUsers.delete(userId)
  }
}

export const resumeTeamCreation = async (
  { userId }: { userId: string },
  finalize?: FinalizeTeamCreation,
) => {
  const attempt = readStoredAttempt(userId)
  if (!attempt) throw new Error('Aucune tentative de création à reprendre')
  if (activeUsers.has(userId)) {
    throw new TeamCreationRecoveryBlockedError(attempt.organizationId)
  }

  activeUsers.add(userId)
  try {
    if (attempt.phase === 'organization_unconfirmed') {
      const result = (await client.graphql({
        query: getOrganizationSummaryQuery,
        variables: { id: attempt.organizationId },
      })) as GraphQLResult<{
        getOrganization?: {
          id: string
          name: string
          email?: string | null
        } | null
      }>
      const organization = result.data?.getOrganization
      if (
        !organization ||
        organization.id !== attempt.organizationId ||
        organization.name !== attempt.organizationName ||
        (attempt.organizationEmail &&
          organization.email !== attempt.organizationEmail)
      ) {
        throw new TeamCreationRecoveryBlockedError(attempt.organizationId)
      }
      attempt.phase = 'seat_pending'
      saveAttempt(attempt)
    }

    await assignInitialSeat(attempt)
    await completeAttempt(attempt, finalize)
    return attempt.organizationId
  } finally {
    activeUsers.delete(userId)
  }
}
